import React from 'react';
import { ArrowDown, Trophy } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import BracketBoard, {
  ROUND_LABEL_HEIGHT,
  COMPACT_ROW_HEIGHT,
  COMPACT_COL_WIDTH,
  COMPACT_CONNECTOR_WIDTH,
} from '../brackets/BracketBoard';
import MatchCard from '../brackets/MatchCard';
import { ElbowPairConnector, SingleLineConnector } from '../brackets/BracketConnector';

const regionLabelFromPlaceholder = (name) => (name || '').replace('Winner of ', '');

// Fixed pixel metrics — kept in sync with the actual Tailwind classes used
// below (border/padding/label heights are set explicitly, not left to text
// line-height, specifically so these numbers are trustworthy for computing
// exactly where a connector line should land instead of estimating).
const REGION_BORDER_PADDING = 17; // 1px border + p-4 (16px)
const REGION_LABEL_HEIGHT = 32;
const REGION_GAP = 16;
const CENTER_BORDER_PADDING = 13; // 1px border + p-3 (12px)
const CENTER_LABEL_HEIGHT = 24;
const MATCH_CARD_COMPACT_HEIGHT = 76; // MatchCard's own rendered height in compact mode
const CENTER_MATCH_HEIGHT = CENTER_BORDER_PADDING * 2 + CENTER_LABEL_HEIGHT + MATCH_CARD_COMPACT_HEIGHT;
const CONNECTOR_WIDTH = 32;
const CENTER_STACK_WIDTH = 208;
// Vertical gap (holding a straight connector line) between two cards stacked
// directly on top of each other in the same column — e.g. Semifinal into
// Championship once both sit in one column instead of side-by-side.
const STACK_CONNECTOR_HEIGHT = 28;

const regionMatchesHeight = (region) =>
  Math.max(...region.rounds.map((r) => r.matches.length)) * COMPACT_ROW_HEIGHT;

const regionBlockHeight = (region) => REGION_BORDER_PADDING * 2 + REGION_LABEL_HEIGHT + ROUND_LABEL_HEIGHT + regionMatchesHeight(region);

// Exact rendered width of a region's own BracketBoard content (round columns +
// connectors between them) plus the RegionCard's own border/padding — sized
// from BracketBoard's real constants rather than a guessed fixed width, so
// the card is never a few px too narrow and doesn't trigger its own internal
// horizontal scrollbar.
const regionCardWidth = (region) => {
  const numRounds = region.rounds.length;
  const content = numRounds * COMPACT_COL_WIDTH + (numRounds - 1) * COMPACT_CONNECTOR_WIDTH;
  return content + REGION_BORDER_PADDING * 2;
};

// The Final round of any region bracket always has exactly 1 match, so its
// own space-around position is dead center of the matches-box — this is the
// exact Y (relative to the region block's own top) a connector should aim for.
const regionFinalCenterOffset = (region) =>
  REGION_BORDER_PADDING + REGION_LABEL_HEIGHT + ROUND_LABEL_HEIGHT + regionMatchesHeight(region) / 2;

/** Stacks a list of regions top-to-bottom with a fixed gap, returning each one's absolute top + Final-card Y. */
const stackRegions = (regionList) => {
  let cumulativeTop = 0;
  const items = regionList.map((region) => {
    const height = regionBlockHeight(region);
    const item = { region, top: cumulativeTop, height, finalY: cumulativeTop + regionFinalCenterOffset(region) };
    cumulativeTop += height + REGION_GAP;
    return item;
  });
  return { items, stackHeight: cumulativeTop - REGION_GAP };
};

const RegionCard = ({ region, mirrored, theme }) => (
  // `border-current` + `theme.text` — same technique as MatchCard/CenterMatch
  // — this panel sits directly on the wizard's own outer panel, which uses
  // the identical `theme.card` token, so `theme.border` (close in shade to
  // `theme.card`'s own fill) wasn't enough contrast in every theme.
  <div className={`rounded-xl border border-current ${theme.text} ${theme.card} p-4`}>
    <div
      className={`flex items-center text-sm font-bold ${theme.text} ${mirrored ? 'justify-end' : ''}`}
      style={{ height: REGION_LABEL_HEIGHT }}
    >
      {region.label}
    </div>
    <BracketBoard rounds={mirrored ? [...region.rounds].reverse() : region.rounds} compact />
  </div>
);

const CenterMatch = ({ label, match, highlighted }) => {
  const { theme } = useTheme();
  return (
    <div
      // `border-current` + `theme.text` — same technique as MatchCard's own
      // border (see that component) — so this reads clearly against either
      // fill (`theme.tabActive` highlighted, `theme.card` otherwise)
      // regardless of theme, instead of relying on a border shade that
      // happens to contrast with one specific background.
      className={`rounded-xl border p-3 border-current ${theme.text} ${theme.shadow} ${highlighted ? theme.tabActive : theme.card}`}
    >
      <div
        className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide ${highlighted ? '' : theme.textMuted}`}
        style={{ height: CENTER_LABEL_HEIGHT }}
      >
        {highlighted && <Trophy size={13} />}
        {label}
      </div>
      <MatchCard match={match} compact />
    </div>
  );
};

/** Absolutely-positions a CenterMatch so its own vertical center lands at `centerY` within the shared coordinate frame. */
const PositionedCenterMatch = ({ centerY, width, ...props }) => (
  <div className="absolute" style={{ top: centerY - CENTER_MATCH_HEIGHT / 2, width }}>
    <CenterMatch {...props} />
  </div>
);

// Two cards stacked directly on top of each other in the same column share an
// X position, so connecting them is just a straight line — no elbow needed.
const StackConnector = ({ theme }) => (
  <div className="flex justify-center" style={{ height: STACK_CONNECTOR_HEIGHT, width: CENTER_STACK_WIDTH }}>
    <div className={`w-px ${theme.textMuted} bg-current opacity-60`} />
  </div>
);

/**
 * Authentic converging bracket layout — two halves facing each other, region
 * winners meeting in a centered Final Stage, matching the classic NCAA
 * tournament poster look (as opposed to CompassGrid's diverging layout).
 * Every card is connected: region-internal connectors come from BracketBoard
 * itself, and this component draws the "bridge" connectors from each
 * region's Final into the Semifinal/Championship stage, using exact pixel
 * offsets (not estimated) so the lines land precisely on card centers.
 * Handled explicitly for 2 and 4 regions, the two shapes that actually have
 * a clean bilateral mirror; other region counts fall back to the simpler
 * stacked-grid layout below rather than forcing an asymmetric "mirror."
 */
const RegionalBracketView = ({ regions, finalStage }) => {
  const { theme } = useTheme();
  const intro = (
    <p className={`mb-4 text-sm ${theme.textSecondary}`}>
      {regions.length} regions play down to a champion independently — then the {regions.length} region winners
      meet in the Final Stage. Bracket shape is fully derived from participant count, nothing to configure.
    </p>
  );

  if (regions.length === 2) {
    const [left, right] = regions;
    const championship = finalStage[finalStage.length - 1].matches[0];
    const yLeft = regionFinalCenterOffset(left);
    const yRight = regionFinalCenterOffset(right);
    const containerHeight = Math.max(regionBlockHeight(left), regionBlockHeight(right), CENTER_MATCH_HEIGHT);
    const yChampionship = (yLeft + yRight) / 2;
    const leftWidth = regionCardWidth(left);
    const rightWidth = regionCardWidth(right);

    return (
      <div>
        {intro}
        {/* One scroll container for the whole drawing (not one per region) —
            region cards are sized to their exact content width above, so this
            only ever scrolls in the rare case the full width still can't fit. */}
        <div className="hidden overflow-x-auto pb-2 lg:block">
          <div className="flex items-start gap-0" style={{ minHeight: containerHeight }}>
            <div style={{ width: leftWidth }}>
              <RegionCard region={left} theme={theme} />
            </div>
            <SingleLineConnector yFrom={yLeft} yTo={yChampionship} height={containerHeight} width={CONNECTOR_WIDTH} colorClassName={theme.textMuted} />
            <div className="relative" style={{ width: 208, height: containerHeight }}>
              <PositionedCenterMatch centerY={yChampionship} width={208} label="Championship" match={championship} highlighted />
            </div>
            <SingleLineConnector yFrom={yChampionship} yTo={yRight} height={containerHeight} width={CONNECTOR_WIDTH} colorClassName={theme.textMuted} reversed />
            <div style={{ width: rightWidth }}>
              <RegionCard region={right} mirrored theme={theme} />
            </div>
          </div>
        </div>
        <div className="space-y-4 lg:hidden">
          <RegionCard region={left} theme={theme} />
          <CenterMatch label="Championship" match={championship} highlighted />
          <RegionCard region={right} mirrored theme={theme} />
        </div>
      </div>
    );
  }

  if (regions.length === 4 && finalStage.length === 2) {
    const [semiLeftMatch, semiRightMatch] = finalStage[0].matches;
    const championship = finalStage[1].matches[0];
    const leftLabels = [regionLabelFromPlaceholder(semiLeftMatch.slotA?.name), regionLabelFromPlaceholder(semiLeftMatch.slotB?.name)];
    const leftRegions = regions.filter((r) => leftLabels.includes(r.label));
    const rightRegions = regions.filter((r) => !leftLabels.includes(r.label));

    const left = stackRegions(leftRegions);
    const right = stackRegions(rightRegions);
    // Semifinal / Championship / Semifinal now render as ONE vertical stack
    // (not three side-by-side columns), so their Y positions come from that
    // stack's own fixed rhythm — centered within containerHeight — rather
    // than from the region convergence points directly. The connectors from
    // each region pair then bend to land wherever the stack actually put its
    // Semifinal card (see ElbowPairConnector's `yExit`).
    const centerStackHeight = CENTER_MATCH_HEIGHT * 3 + STACK_CONNECTOR_HEIGHT * 2;
    const containerHeight = Math.max(left.stackHeight, right.stackHeight, centerStackHeight);
    const centerStackTop = (containerHeight - centerStackHeight) / 2;
    const ySemiLeft = centerStackTop + CENTER_MATCH_HEIGHT / 2;
    const ySemiRight = centerStackTop + 2 * (CENTER_MATCH_HEIGHT + STACK_CONNECTOR_HEIGHT) + CENTER_MATCH_HEIGHT / 2;
    // Stacked regions share one column, so use the wider of the two so both
    // cards in a stack render at the same (sufficient) width.
    const leftWidth = Math.max(...leftRegions.map(regionCardWidth));
    const rightWidth = Math.max(...rightRegions.map(regionCardWidth));

    return (
      <div>
        {intro}
        <div className="hidden overflow-x-auto pb-2 lg:block">
          <div className="flex items-start gap-0" style={{ minHeight: containerHeight }}>
            <div className="relative" style={{ width: leftWidth, height: containerHeight }}>
              {left.items.map(({ region, top }) => (
                <div key={region.label} className="absolute" style={{ top, width: leftWidth }}>
                  <RegionCard region={region} theme={theme} />
                </div>
              ))}
            </div>
            <ElbowPairConnector
              yTop={left.items[0].finalY}
              yBottom={left.items[1].finalY}
              yExit={ySemiLeft}
              height={containerHeight}
              width={CONNECTOR_WIDTH}
              colorClassName={theme.textMuted}
            />
            <div className="flex flex-col justify-center" style={{ width: CENTER_STACK_WIDTH, height: containerHeight }}>
              <CenterMatch label="Semifinal" match={semiLeftMatch} />
              <StackConnector theme={theme} />
              <CenterMatch label="Championship" match={championship} highlighted />
              <StackConnector theme={theme} />
              <CenterMatch label="Semifinal" match={semiRightMatch} />
            </div>
            <ElbowPairConnector
              yTop={right.items[0].finalY}
              yBottom={right.items[1].finalY}
              yExit={ySemiRight}
              height={containerHeight}
              width={CONNECTOR_WIDTH}
              colorClassName={theme.textMuted}
              reversed
            />
            <div className="relative" style={{ width: rightWidth, height: containerHeight }}>
              {right.items.map(({ region, top }) => (
                <div key={region.label} className="absolute" style={{ top, width: rightWidth }}>
                  <RegionCard region={region} mirrored theme={theme} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:hidden">
          {leftRegions.map((region) => (
            <RegionCard key={region.label} region={region} theme={theme} />
          ))}
          <CenterMatch label="Semifinal" match={semiLeftMatch} />
          <CenterMatch label="Championship" match={championship} highlighted />
          <CenterMatch label="Semifinal" match={semiRightMatch} />
          {rightRegions.map((region) => (
            <RegionCard key={region.label} region={region} theme={theme} />
          ))}
        </div>
      </div>
    );
  }

  // Fallback for region counts without a clean bilateral mirror (3, 5, 6, 7, 8+).
  return (
    <div>
      {intro}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {regions.map((region) => (
          <RegionCard key={region.label} region={region} theme={theme} />
        ))}
      </div>
      <div className="my-6 flex items-center justify-center gap-2">
        <ArrowDown size={16} className={theme.textMuted} />
        <span className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted}`}>
          Region champions advance
        </span>
        <ArrowDown size={16} className={theme.textMuted} />
      </div>
      <div className={`rounded-xl border border-transparent p-4 ${theme.tabActive} ${theme.shadow}`}>
        <div className="mb-3 flex items-center gap-2 text-sm font-bold">
          <Trophy size={16} />
          Final Stage
        </div>
        <BracketBoard rounds={finalStage} />
      </div>
    </div>
  );
};

export default RegionalBracketView;
